<template lang="pug">
b-modal(
  :id="modalId"
  :title="$t('message.rrHistoryTitle')"
  size="xl"
  scrollable
  ok-only
  :ok-title="$t('message.close')"
  @show="loadHistory"
)
  div(v-if="isLoading")
    b-card.py-3
      b-card-body.text-center
        b-spinner(variant="primary")
        p.mt-2 {{ $t('message.loading') }}

  div(v-else-if="!historyItems || historyItems.length === 0")
    b-card
      b-card-body.text-center
        feather-icon(icon="FileTextIcon", size="48", class="text-muted mb-2")
        h5.text-muted {{ $t('message.noEvaluationsFound') }}
        p.text-muted {{ $t('message.noEvaluationsDescription') }}

  div(v-else)
    b-table(
      :items="historyItems"
      :fields="tableColumns"
      striped
      hover
      responsive
      show-empty
      :empty-text="$t('message.no_records')"
    )
      template(#cell(date)="data")
        span {{ formatDate(data.item.date) }}

      template(#cell(evaluationLevel)="data")
        b-badge(:variant="getLevelVariant(data.item.evaluationLevel)")
          | {{ $t(`evaluationLevel.${data.item.evaluationLevel}`) }}

      template(#cell(previousRR)="data")
        span.font-weight-bold(:style="{ color: getRRColor(data.item.previousRR) }")
          | {{ data.item.previousRR }}

      template(#cell(newRR)="data")
        span.font-weight-bold(:style="{ color: getRRColor(data.item.newRR) }")
          | {{ data.item.newRR }}
        feather-icon(
          v-if="data.item.newRR > data.item.previousRR"
          icon="TrendingUpIcon"
          size="16"
          class="ml-1 text-danger"
        )
        feather-icon(
          v-else-if="data.item.newRR < data.item.previousRR"
          icon="TrendingDownIcon"
          size="16"
          class="ml-1 text-success"
        )
        feather-icon(
          v-else
          icon="MinusIcon"
          size="16"
          class="ml-1 text-muted"
        )

      template(#cell(evaluator)="data")
        span {{ data.item.evaluator && data.item.evaluator.name ? data.item.evaluator.name : '-' }}

      template(#cell(evaluated)="data")
        span {{ data.item.evaluated && data.item.evaluated.name ? data.item.evaluated.name : '-' }}

      template(#cell(compliancePercentage)="data")
        span {{ data.item.compliancePercentage != null ? data.item.compliancePercentage.toFixed(1) : '-' }}%
</template>

<script>
import { BModal, BTable, BBadge, BCard, BCardBody, BSpinner } from 'bootstrap-vue'
import { ref, computed } from '@vue/composition-api'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import i18n from '@/libs/i18n'

export default {
  components: {
    BModal,
    BTable,
    BBadge,
    BCard,
    BCardBody,
    BSpinner,
  },
  props: {
    processId: {
      type: String,
      required: true,
    },
    modalId: {
      type: String,
      default: 'rr-history-modal',
    },
  },
  setup(props) {
    const { getItemsAggregate } = useLambda()
    const historyItems = ref([])
    const isLoading = ref(false)

    const tableColumns = computed(() => [
      { key: 'date', label: i18n.t('message.tableHeader.date'), sortable: true },
      { key: 'compliancePercentage', label: i18n.t('message.tableHeader.evaluationResult'), sortable: true },
      { key: 'evaluationLevel', label: i18n.t('message.tableHeader.level'), sortable: true },
      { key: 'previousRR', label: i18n.t('message.tableHeader.residualRiskPrevious'), sortable: true },
      { key: 'newRR', label: i18n.t('message.tableHeader.residualRiskNew'), sortable: true },
      { key: 'evaluator', label: i18n.t('label.evaluator'), sortable: false },
      { key: 'evaluated', label: i18n.t('label.evaluated'), sortable: false },
    ])

    const loadHistory = async () => {
      if (!props.processId) return

      isLoading.value = true
      try {
        const pipeline = [
          { $match: {
            process: oid(props.processId),
            previousRR: { $exists: true },
            newRR: { $exists: true },
            pending: { $ne: true }
          }},
          { $lookup: {
            from: 'worker',
            localField: 'supervisor',
            foreignField: '_id',
            pipeline: [{ $project: { name: 1 }}],
            as: 'evaluator'
          }},
          { $lookup: {
            from: 'worker',
            localField: 'worker',
            foreignField: '_id',
            pipeline: [{ $project: { name: 1 }}],
            as: 'evaluated'
          }},
          { $addFields: {
            evaluator: { $arrayElemAt: ['$evaluator', 0] },
            evaluated: { $arrayElemAt: ['$evaluated', 0] }
          }},
          { $sort: { date: -1 }}
        ]

        const items = await getItemsAggregate({
          collection: 'confirmation',
          pipeline
        })

        historyItems.value = items || []
      } catch (error) {
        console.error('Error loading RR history:', error)
        historyItems.value = []
      } finally {
        isLoading.value = false
      }
    }

    const formatDate = (date) => {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }

    const getRRColor = (rr) => {
      if (rr <= 5) return '#006400'
      if (rr <= 10) return '#2e8b57'
      if (rr <= 15) return '#d4a017'
      if (rr <= 20) return '#ff8c00'
      return '#cc0000'
    }

    const getLevelVariant = (level) => {
      const variants = {
        optimal: 'success',
        good: 'info',
        insufficient: 'warning',
        critical: 'danger'
      }
      return variants[level] || 'secondary'
    }

    return {
      historyItems,
      isLoading,
      tableColumns,
      loadHistory,
      formatDate,
      getRRColor,
      getLevelVariant,
    }
  },
}
</script>

<style scoped>
.text-muted {
  color: #6c757d !important;
}
</style>